/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.15.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '../../../../../assets';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import { EOnboardingComponentsId } from '../../../../../modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { ExternalRoutePath, openExternalRoute } from '../../../../../routes/constants/externalRoutes';
import { IRouteParams, RoutePath, SUBSCRIBER_ID } from '../../../../../routes/constants/routesConstants';
import { stringFormat } from '../../../../../utils/stringFormat';
import { TableFooterNameType } from '../../../../Table/types/tableTypes';
import {
    TTableButtonsGetTranslation,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';


const useTableFooterButtonAdditionalAccount = (
    getTranslation: TTableButtonsGetTranslation,
    tableFooterName: TableFooterNameType,
) => {
    const dispatch = useDispatch();
    const { clientId } = useParams<IRouteParams>();

    const isWizardAdditionalAccountsEnabled =
        useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardAdditionalAccounts));

    return {
        className: 'table-footer__link link-calls',
        dataName: 'calls',
        icon: icons.callsSliderIcon,
        id: AccessComponentKeyEnum.FooterButtonAdditionalAccount,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonAdditionalAccount),

        onClick: useCallback(() => {
            dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.ADDITIONAL_ACCOUNTS }));
            const urlConfig = mapTableFooterNameToUrl[tableFooterName];
            if (!urlConfig) return;

            isWizardAdditionalAccountsEnabled
                ? appHistoryPush(urlConfig.url.replace(SUBSCRIBER_ID, clientId))
                : openExternalRoute(stringFormat(urlConfig.external, clientId));
        }, [clientId, dispatch, isWizardAdditionalAccountsEnabled, tableFooterName]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.subscriberId]: {
        url: RoutePath.wizardAdditionalAccountsFromSubscriberId,
        external: ExternalRoutePath.mobileAdditionalAccountsId,
    },
    [TableFooterNameType.subscriberMobile]: {
        url: RoutePath.wizardAdditionalAccountsFromSubscribers,
        external: ExternalRoutePath.mobileAdditionalAccounts,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonAdditionalAccount,
    },
};

export default useTableFooterButtonAdditionalAccount;
